import { useState } from "react";
import { PHONE, PHONE_HREF, EMAIL, FORM_ACTION } from "../../mocks/businessData";

interface FinalCTAProps {
  heading?: string;
  subtext?: string;
  bgImage?: string;
}

export default function FinalCTA({
  heading = "Ready to Transform Your Outdoor Space?",
  subtext = "London homeowners trust Upper Canada Landscape for exceptional outdoor living design and construction. Let\u2019s talk about your project.",
  bgImage = "https://uppercanadalandscape.com/wp-content/uploads/2026/03/Modern-Backyard-Pool-House-and-Pool-Landscape-Design.webp",
}: FinalCTAProps) {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    postal: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.message) return;
    setStatus("sending");
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        postal: formData.postal,
        message: formData.message,
      });
      const res = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", phone: "", postal: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt="Premium landscaping background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-charcoal/82"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: CTA copy */}
          <div className="text-white">
            <span className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-rust-400 mb-4 block">
              Get Started Today
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              {heading}
            </h2>
            <p className="font-sans text-base text-stone-300 leading-relaxed mb-8">
              {subtext}
            </p>

            <div className="space-y-4 mb-10">
              {[
                "No-obligation design consultation",
                "Custom project proposal & pricing",
                "Transparent scope, no hidden costs",
                "In-house crews \u2014 no subcontracting",
              ].map((point) => (
                <div key={point} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-fill text-rust-400 text-lg"></i>
                  </div>
                  <span className="font-sans text-sm text-stone-200">{point}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={PHONE_HREF}
                className="flex items-center justify-center gap-2 bg-white text-charcoal font-sans text-sm font-semibold px-6 py-3.5 rounded-sm hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-rust-700"></i>
                Call {PHONE}
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center justify-center gap-2 border border-white/30 text-white font-sans text-sm font-semibold px-6 py-3.5 rounded-sm hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-fill"></i>
                Email Us
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-xl p-6 md:p-8">
            <h3 className="font-serif text-2xl font-bold text-charcoal mb-2">
              Schedule Your Consultation
            </h3>
            <p className="font-sans text-sm text-stone-500 mb-6">
              Tell us about your project and we&apos;ll be in touch within one business day.
            </p>

            {status === "success" ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl mb-3 block"></i>
                <p className="font-sans font-semibold text-charcoal mb-1">Request Received!</p>
                <p className="font-sans text-sm text-stone-500">
                  We&apos;ll be in touch shortly. You can also call us directly at {PHONE}.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-sans text-xs font-semibold text-charcoal uppercase tracking-wide mb-1.5 block">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="w-full border border-stone-200 rounded-sm px-3 py-2.5 font-sans text-sm text-charcoal placeholder-stone-400 focus:outline-none focus:border-rust-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-sans text-xs font-semibold text-charcoal uppercase tracking-wide mb-1.5 block">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@email.com"
                      required
                      className="w-full border border-stone-200 rounded-sm px-3 py-2.5 font-sans text-sm text-charcoal placeholder-stone-400 focus:outline-none focus:border-rust-500 transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-sans text-xs font-semibold text-charcoal uppercase tracking-wide mb-1.5 block">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(519) 000-0000"
                      required
                      className="w-full border border-stone-200 rounded-sm px-3 py-2.5 font-sans text-sm text-charcoal placeholder-stone-400 focus:outline-none focus:border-rust-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-sans text-xs font-semibold text-charcoal uppercase tracking-wide mb-1.5 block">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="postal"
                      value={formData.postal}
                      onChange={handleChange}
                      placeholder="N6A 1B1"
                      className="w-full border border-stone-200 rounded-sm px-3 py-2.5 font-sans text-sm text-charcoal placeholder-stone-400 focus:outline-none focus:border-rust-500 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-sans text-xs font-semibold text-charcoal uppercase tracking-wide mb-1.5 block">
                    Project Details *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us what you have in mind for your outdoor space..."
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full border border-stone-200 rounded-sm px-3 py-2.5 font-sans text-sm text-charcoal placeholder-stone-400 focus:outline-none focus:border-rust-500 transition-colors resize-none"
                  />
                  <p className="font-sans text-xs text-stone-400 text-right mt-1">
                    {formData.message.length}/500
                  </p>
                </div>

                {status === "error" && (
                  <p className="font-sans text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    Something went wrong. Please call us at {PHONE}.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="whitespace-nowrap w-full bg-rust-700 hover:bg-rust-800 disabled:bg-stone-300 text-white font-sans text-sm font-semibold py-3.5 rounded-sm transition-colors cursor-pointer"
                >
                  {status === "sending" ? "Sending..." : "Send My Project Request"}
                </button>

                <p className="font-sans text-xs text-stone-400 text-center">
                  We respond within 1 business day &bull; No obligation
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
